class RuffTheme {
  constructor() {
    this.settings = window.ruffTheme || {};
    this.strings = this.settings.strings || {};
    this.rootUrl = this.settings.rootUrl || '/';
    this.cartUrl = this.settings.cartUrl || '/cart';
    this.cartType = this.settings.cartType || 'drawer';
    this.overlay = document.querySelector('[data-overlay]');
    this.panels = [...document.querySelectorAll('[data-panel]')];
    this.noteTimer = null;
    this.searchTimer = null;
    this.searchController = null;
    this.bindDrawers();
    this.bindQuickAdd();
    this.bindCartChanges();
    this.bindCartNote();
    this.bindPredictiveSearch();
    this.bindStickyHeader();
    this.bindFooterAccordion();
    this.bindGalleryCounter();
    this.bindWishlist();
    this.bindSizeGuide();
    this.bindScrollMemory();
    this.bindSizeGuidePanel();
    this.bindProductZoom();
  }

  // ---- product image viewer ----

  // Click any gallery image to open it full screen, then swipe or use the
  // arrows. Slides are built from the gallery's own <img> elements the first
  // time it opens, so the URLs and their srcsets come from Liquid and there
  // is no second list of images to maintain.
  bindProductZoom() {
    const zoom = document.querySelector('[data-product-zoom]');
    const gallery = document.querySelector('.product-gallery');
    const stage = zoom && zoom.querySelector('[data-zoom-stage]');
    if (!zoom || !gallery || !stage) return;

    const sources = () => [...gallery.querySelectorAll('.product-media:not(.product-media--filler) img')];
    const navs = [...zoom.querySelectorAll('[data-zoom-step]')];
    let built = false;
    let lastFocused = null;

    const build = () => {
      if (built) return;
      stage.replaceChildren(
        ...sources().map((img) => {
          const slide = document.createElement('div');
          slide.className = 'product-zoom-slide';
          const clone = document.createElement('img');
          clone.src = img.currentSrc || img.src;
          if (img.srcset) clone.srcset = img.srcset;
          clone.sizes = '100vw';
          clone.alt = img.alt || '';
          slide.appendChild(clone);
          return slide;
        })
      );
      built = true;
    };

    const index = () => Math.round(stage.scrollLeft / stage.clientWidth);

    const syncNav = () => {
      const count = stage.children.length;
      const at = index();
      navs.forEach((btn) => {
        const step = Number(btn.dataset.zoomStep);
        const target = at + step;
        btn.disabled = target < 0 || target > count - 1;
      });
    };

    const goTo = (i, smooth = true) => {
      const count = stage.children.length;
      const clamped = Math.max(0, Math.min(count - 1, i));
      stage.scrollTo({ left: clamped * stage.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
    };

    const open = (from) => {
      build();
      lastFocused = document.activeElement;
      zoom.hidden = false;
      document.body.classList.add('drawer-open');
      goTo(from, false);
      syncNav();
      stage.focus({ preventScroll: true });
    };

    const close = () => {
      zoom.hidden = true;
      document.body.classList.remove('drawer-open');
      if (lastFocused) lastFocused.focus();
    };

    gallery.addEventListener('click', (event) => {
      // The wishlist button lives in the gallery too and must keep its own job.
      if (event.target.closest('[data-wishlist-toggle]')) return;
      const media = event.target.closest('.product-media');
      if (!media || media.classList.contains('product-media--filler')) return;
      const img = media.querySelector('img');
      if (!img) return;
      open(sources().indexOf(img));
    });

    navs.forEach((btn) => {
      btn.addEventListener('click', () => goTo(index() + Number(btn.dataset.zoomStep)));
    });
    zoom.querySelector('[data-zoom-close]').addEventListener('click', close);
    stage.addEventListener('scroll', () => window.requestAnimationFrame(syncNav), { passive: true });

    document.addEventListener('keydown', (event) => {
      if (zoom.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') goTo(index() + 1);
      if (event.key === 'ArrowLeft') goTo(index() - 1);
    });
  }

  // ---- size guide panel ----

  // On phones the PDP's size-guide link opens a panel instead of navigating
  // away mid-purchase. The content is fetched from the page the link already
  // points at, so the guide is authored once and this stays in step with it.
  // Anything wider — and anything without JS — just follows the link.
  bindSizeGuidePanel() {
    const panel = document.querySelector('[data-panel="size-guide"]');
    const body = panel && panel.querySelector('[data-size-guide-body]');
    if (!panel || !body) return;

    let loaded = false;

    const load = async (url) => {
      if (loaded) return;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const guide = parsed.querySelector('[data-size-guide]');
        if (!guide) throw new Error('no size guide section on that page');
        body.replaceChildren(guide);
        // Tabs and the cm/inch switch need wiring on this fresh copy.
        this.bindSizeGuide(body);
        loaded = true;
      } catch (error) {
        // Leave the link usable rather than trapping the shopper in an empty
        // panel: close it and let the navigation happen.
        loaded = false;
        const loading = body.querySelector('[data-size-guide-loading]');
        if (loading) loading.textContent = '';
        window.location.href = url;
      }
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-size-guide-link]');
      if (!link) return;
      if (!window.matchMedia('(max-width: 680px)').matches) return;
      event.preventDefault();
      this.openPanel('size-guide');
      load(link.href);
    });
  }

  // ---- scroll memory ----

  // Puts the shopper back where they were when they return to a collection
  // from a product, rather than at the top of the grid.
  //
  // The browser normally handles this, but `html { scroll-behavior: smooth }`
  // in base.css makes its restore unreliable — it either animates the whole
  // way down or lands at the top. So restoration is taken over here: switch
  // the browser to manual, record the position, and put it back explicitly.
  // Only pages that opt in with [data-scroll-restore] are affected.
  bindScrollMemory() {
    if (!document.querySelector('[data-scroll-restore]')) return;

    // Query string included: filtered and paginated views are different pages
    // to a shopper and each deserves its own remembered position.
    const key = `ruff:scroll:${location.pathname}${location.search}`;

    const read = () => {
      try {
        const raw = sessionStorage.getItem(key);
        return raw === null ? null : parseInt(raw, 10);
      } catch (e) {
        return null;
      }
    };

    const save = () => {
      try {
        sessionStorage.setItem(key, String(Math.round(window.scrollY)));
      } catch (e) {
        /* private mode or storage disabled — restoration just won't happen */
      }
    };

    const restore = (y) => {
      if (!Number.isFinite(y)) return;
      // 'instant' overrides the smooth-scroll default, which would otherwise
      // animate the page all the way down in front of the shopper.
      window.scrollTo({ top: y, behavior: 'instant' });
    };

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // pagehide fires for a normal navigation and for entering the back/forward
    // cache; the visibility backstop covers mobile Safari discarding a tab
    // without ever firing it.
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') save();
    });

    // Restored from the back/forward cache: the DOM is intact, only the
    // scroll position needs putting back.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const y = read();
      if (y !== null) restore(y);
    });

    const entry = performance.getEntriesByType('navigation')[0];
    const cameBack = entry ? entry.type === 'back_forward' : false;
    if (!cameBack) return;

    const y = read();
    if (y === null) return;

    restore(y);
    // Lazy images finish loading after this point and can change the page
    // height, so the position is reapplied once everything has settled —
    // otherwise a long grid lands short of where it was.
    window.addEventListener(
      'load',
      () => window.requestAnimationFrame(() => restore(y)),
      { once: true }
    );
  }

  // ---- size guide ----

  // Tabs plus a centimetre/inch switch. Scoped per section so more than one
  // size guide on a page cannot drive each other's panels.
  bindSizeGuide(root = document) {
    root.querySelectorAll('[data-size-guide]').forEach((guide) => {
      // The panel copy is bound after it is fetched, so guard against a
      // second pass wiring the same tabs twice.
      if (guide.dataset.sizeGuideBound) return;
      guide.dataset.sizeGuideBound = 'true';
      const tabs = [...guide.querySelectorAll('[data-size-tab]')];
      const panels = [...guide.querySelectorAll('[data-size-panel]')];

      const select = (index, { focus = false } = {}) => {
        tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', String(on));
          // Roving tabindex: one stop for the whole tablist, then arrow keys
          // move between tabs — the expected pattern for this widget.
          tab.tabIndex = on ? 0 : -1;
          if (panels[i]) panels[i].hidden = !on;
        });
        if (focus && tabs[index]) tabs[index].focus();
      };

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(i));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity }[event.key];
          if (step === undefined) return;
          event.preventDefault();
          let next;
          if (step === -Infinity) next = 0;
          else if (step === Infinity) next = tabs.length - 1;
          else next = (i + step + tabs.length) % tabs.length;
          select(next, { focus: true });
        });
      });

      // ---- region picker ----

      // The second column carries every region's value at once; the picker
      // decides which one is shown. No fetch, no reload.
      const region = guide.querySelector('[data-size-region]');
      if (region) {
        const swap = () => {
          guide.querySelectorAll('.size-guide-region-value').forEach((cell) => {
            cell.hidden = cell.dataset.region !== region.value;
          });
        };
        region.addEventListener('change', swap);
        swap();
      }

      // ---- units ----

      const cells = [...guide.querySelectorAll('[data-size-cm]')];
      if (!cells.length) return;

      const toInches = (cm) => {
        const value = parseFloat(cm);
        if (!Number.isFinite(value)) return null;
        // One decimal, with a trailing .0 trimmed so whole numbers stay clean.
        return String(Math.round((value / 2.54) * 10) / 10);
      };

      guide.querySelectorAll('[data-size-unit]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          const inches = input.value === 'in';
          cells.forEach((cell) => {
            const cm = cell.dataset.sizeCm;
            if (!inches) {
              cell.textContent = cm;
              return;
            }
            const converted = toInches(cm);
            // Anything non-numeric (a dash, a range) is left exactly as typed
            // rather than turned into NaN.
            cell.textContent = converted === null ? cm : converted;
          });
        });
      });
    });
  }

  // ---- wishlist ----

  // Shopify has no wishlist, so this keeps one in the browser. It is
  // per-device and per-browser by nature — a real cross-device list needs an
  // app, which can take over by binding to the same [data-wishlist-toggle]
  // hook and calling preventDefault first.
  //
  // Delegated from the document so cards injected later — the related
  // products fetch, any paginated grid — pick the behaviour up for free.
  bindWishlist() {
    const KEY = 'ruff:wishlist';

    const read = () => {
      try {
        const raw = JSON.parse(localStorage.getItem(KEY));
        return Array.isArray(raw) ? raw : [];
      } catch (e) {
        // Private mode, disabled storage, or someone else's malformed value.
        return [];
      }
    };

    const write = (list) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — the toggle still works for this pageview */
      }
    };

    const paint = (root = document) => {
      const saved = read();
      root.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(saved.includes(btn.dataset.wishlistHandle)));
      });
    };

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      event.preventDefault();

      const handle = btn.dataset.wishlistHandle;
      if (!handle) return;

      const saved = read();
      const at = saved.indexOf(handle);
      if (at === -1) saved.push(handle);
      else saved.splice(at, 1);
      write(saved);

      // Repaint every card, not just this one: the same product can appear
      // twice on a page (grid plus related products).
      paint();
      document.dispatchEvent(
        new CustomEvent('ruff:wishlist:change', { detail: { handle, saved } })
      );
    });

    paint();
    // Another tab may have changed the list.
    window.addEventListener('storage', (event) => {
      if (event.key === KEY) paint();
    });
  }

  // ---- product gallery scrub bar ----

  // Mirrors the mobile carousel: the thumb is one slide wide and slides
  // across the rail in proportion to how far the track has scrolled.
  bindGalleryCounter() {
    const gallery = document.querySelector('.product-gallery');
    const rail = document.querySelector('[data-gallery-progress]');
    const thumb = document.querySelector('[data-gallery-thumb]');
    if (!gallery || !rail || !thumb) return;

    const slides = gallery.querySelectorAll('.product-media').length;
    if (slides < 2) return;

    let ticking = false;
    const update = () => {
      const railWidth = rail.clientWidth;
      const thumbWidth = railWidth / slides;
      const scrollable = gallery.scrollWidth - gallery.clientWidth;
      const progress = scrollable > 0 ? gallery.scrollLeft / scrollable : 0;
      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${progress * (railWidth - thumbWidth)}px)`;
      ticking = false;
    };

    update();
    gallery.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  // ---- footer accordion ----

  // Footer columns ship with `open` so they render expanded on desktop and
  // without JS. Below the breakpoint they collapse into an accordion.
  bindFooterAccordion() {
    const cols = [...document.querySelectorAll('.theme-footer-links .footer-col')];
    if (!cols.length) return;

    const mq = window.matchMedia('(max-width: 1040px)');
    const apply = () => cols.forEach((col) => { col.open = !mq.matches; });

    apply();
    mq.addEventListener('change', apply);
  }

  // ---- sticky header ----

  // The header sits transparent over the homepage hero and turns solid once
  // the announcement bar has scrolled away. Interior templates start solid,
  // so they only need the state for the announcement offset.
  bindStickyHeader() {
    const headers = [...document.querySelectorAll('[data-sticky-header]')];
    if (!headers.length) return;

    const announcement = document.querySelector('.theme-announcement');
    const threshold = announcement ? announcement.offsetHeight : 34;
    let ticking = false;

    const apply = () => {
      const stuck = window.scrollY > threshold;
      headers.forEach((header) => header.classList.toggle('is-stuck', stuck));
      ticking = false;
    };

    apply();
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }, { passive: true });
  }

  routeUrl(path) {
    const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
    return `${root}${path.replace(/^\//, '')}`;
  }

  // ---- drawers ----

  bindDrawers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-panel]');
      const closer = event.target.closest('[data-close-panel]');
      if (trigger) {
        event.preventDefault();
        const name = trigger.dataset.openPanel;
        // The menu now opens below the header, so the hamburger that opened it
        // stays on screen and in reach. Pressing it again has to close the
        // panel rather than tear it down and slide it straight back out.
        const panel = document.querySelector(`[data-panel="${name}"]`);
        if (panel?.classList.contains('is-open')) this.closePanels();
        else this.openPanel(name);
      }
      if (closer || event.target === this.overlay) this.closePanels();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePanels();
    });
  }

  openPanel(name) {
    this.closePanels();
    const panel = document.querySelector(`[data-panel="${name}"]`);
    if (!panel) return;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-active');
    document.body.classList.add('drawer-open');
    // The menu is the one panel the header sits above rather than behind, so
    // it gets its own flag: the bar has to drop its transparency and take the
    // menu's colour while it is out.
    document.body.classList.toggle('menu-open', name === 'menu');
    document.querySelector('.theme-mobile-menu-toggle')?.setAttribute('aria-expanded', String(name === 'menu'));
    // The menu's first focusable is now its search field, and focusing that
    // throws up the keyboard on a phone the moment the menu opens — and hides
    // the caret hint behind the real one. Move into the links instead; the
    // search drawer still lands straight in its input, which is the point of it.
    const target =
      name === 'menu' ? panel.querySelector('nav a') : panel.querySelector('button, input, a');
    target?.focus();
  }

  closePanels() {
    this.panels.forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    this.overlay?.classList.remove('is-active');
    document.body.classList.remove('drawer-open');
    document.body.classList.remove('menu-open');
    document.querySelector('.theme-mobile-menu-toggle')?.setAttribute('aria-expanded', 'false');
  }

  // ---- quick add ----

  bindQuickAdd() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-quick-add]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const error = form.querySelector('[data-error]');
      error?.classList.remove('is-visible');
      button?.setAttribute('aria-busy', 'true');
      if (button && this.strings.adding) {
        if (!button.dataset.restLabel) button.dataset.restLabel = button.textContent;
        button.textContent = this.strings.adding;
      }
      try {
        const response = await fetch(this.routeUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Cart add failed');
        await this.refreshCart();
        if (button && this.strings.added) button.textContent = this.strings.added;
        if (this.cartType !== 'page') this.openPanel('cart');
      } catch {
        if (error) {
          if (this.strings.cartError) error.textContent = this.strings.cartError;
          error.classList.add('is-visible');
        }
        this.announce(this.strings.cartError);
      } finally {
        button?.removeAttribute('aria-busy');
        if (button && button.dataset.restLabel) {
          window.setTimeout(() => {
            button.textContent = button.dataset.restLabel;
            delete button.dataset.restLabel;
          }, 1500);
        }
      }
    });
  }

  // ---- cart line changes (remove links + quantity steppers) ----

  bindCartChanges() {
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.changeLine(Number(remove.dataset.cartRemove), 0, remove);
        return;
      }
      const step = event.target.closest('[data-qty-change]');
      if (step) {
        event.preventDefault();
        const input = step.closest('[data-qty]')?.querySelector('[data-qty-input]');
        if (!input) return;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyChange));
        input.value = next;
        this.changeLine(Number(input.dataset.line), next, step);
      }
    });
    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-qty-input]');
      if (!input) return;
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = quantity;
      this.changeLine(Number(input.dataset.line), quantity, input);
    });
  }

  async changeLine(line, quantity, sourceElement) {
    if (!line) return;
    const scope = sourceElement?.closest('[data-qty], td, article') || sourceElement;
    scope?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.routeUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!response.ok) throw new Error('Cart change failed');
      await this.refreshCart();
    } catch {
      this.announce(this.strings.cartError);
    } finally {
      scope?.removeAttribute('aria-busy');
    }
  }

  // ---- cart note ----

  bindCartNote() {
    document.addEventListener('input', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => this.saveNote(note.value), 500);
    });
    document.addEventListener('change', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      window.clearTimeout(this.noteTimer);
      this.saveNote(note.value);
    });
  }

  async saveNote(note) {
    try {
      await fetch(this.routeUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch {
      /* leave the typed note in place; it will submit with the form */
    }
  }

  // ---- cart re-rendering ----

  async refreshCart() {
    let cart = null;
    try {
      cart = await fetch(this.routeUrl('cart.js'), { headers: { Accept: 'application/json' } }).then((response) => response.json());
    } catch {
      /* counts keep their last value */
    }
    if (cart) {
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
      });
    }
    const jobs = [];
    const drawerBody = document.querySelector('[data-cart-body]');
    if (drawerBody) {
      const joiner = this.rootUrl.includes('?') ? '&' : '?';
      jobs.push(this.renderSectionInto(`${this.rootUrl}${joiner}section_id=header-editorial`, '[data-cart-body]', drawerBody));
    }
    const cartPage = document.querySelector('[data-cart-page]');
    if (cartPage) {
      const type = cartPage.dataset.sectionType || 'main-cart';
      jobs.push(this.renderSectionInto(`${this.cartUrl}?section_id=${encodeURIComponent(type)}`, '[data-cart-page]', cartPage));
    }
    await Promise.all(jobs);
    return cart;
  }

  async renderSectionInto(url, selector, target) {
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    } catch {
      /* keep current markup */
    }
  }

  // ---- predictive search ----

  bindPredictiveSearch() {
    if (!this.settings.predictiveSearch) return;
    document.addEventListener('input', (event) => {
      const input = event.target.closest('[data-predictive-input]');
      if (!input) return;
      window.clearTimeout(this.searchTimer);
      const results = this.predictiveContainerFor(input);
      if (!results) return;
      const query = input.value.trim();
      if (query.length < 2) {
        this.abortSearch();
        this.hidePredictive(input, results);
        return;
      }
      this.searchTimer = window.setTimeout(() => this.fetchPredictive(query, input, results), 250);
    });
  }

  predictiveContainerFor(input) {
    return input.closest('[data-panel]')?.querySelector('[data-predictive-results]')
      || document.querySelector('[data-predictive-results]');
  }

  abortSearch() {
    this.searchController?.abort();
    this.searchController = null;
  }

  hidePredictive(input, results) {
    results.hidden = true;
    results.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
  }

  async fetchPredictive(query, input, results) {
    this.abortSearch();
    this.searchController = new AbortController();
    const url = `${this.routeUrl('search/suggest')}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,page&resources[limit]=6`;
    try {
      const response = await fetch(url, { signal: this.searchController.signal });
      if (!response.ok) return;
      const html = await response.text();
      if (input.value.trim() !== query) return;
      results.innerHTML = html;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    } catch {
      /* aborted or offline — keep whatever is shown */
    }
  }

  // ---- utilities ----

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
    }).format(cents / 100);
  }

  announce(message) {
    if (!message) return;
    let region = document.querySelector('[data-live-region]');
    if (!region) {
      region = document.createElement('div');
      region.setAttribute('data-live-region', '');
      region.setAttribute('role', 'status');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new RuffTheme());
